import { useEffect } from "react";

/**
 * Drives a circular orbit animation over a set of DOM children.
 * On hover over any child, the direction reverses from its current angle.
 *
 * @param {React.RefObject} containerRef — wrapper whose children are orbited
 * @param {string} childSelector — selector for orbiting children (scoped to container)
 * @param {number} periodSeconds — seconds per full revolution
 * @param {number} fallbackRadius — fallback radius in px if CSS var --orbit-r is absent
 */
export const useOrbit = (
  containerRef,
  childSelector,
  periodSeconds = 180,
  fallbackRadius = 240
) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const children = Array.from(container.querySelectorAll(childSelector));
    if (!children.length) return;

    const n = children.length;
    const readRadius = () => {
      const v = parseFloat(getComputedStyle(children[0]).getPropertyValue("--orbit-r"));
      return isNaN(v) ? fallbackRadius : v;
    };
    const apply = (angle) => {
      const r = readRadius();
      for (let i = 0; i < n; i++) {
        const a = angle + (i / n) * Math.PI * 2 - Math.PI / 2;
        children[i].style.transform = `translate(${Math.cos(a) * r}px, ${Math.sin(a) * r}px)`;
      }
    };

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      apply(0);
      return;
    }

    let angle = 0;
    let direction = 1;
    let raf = 0;
    let last = performance.now();
    const angularSpeed = (Math.PI * 2) / periodSeconds;

    const tick = (t) => {
      const dt = Math.min(0.1, (t - last) / 1000);
      last = t;
      angle += angularSpeed * direction * dt;
      apply(angle);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    let hoverCount = 0;
    const onEnter = () => {
      hoverCount += 1;
      direction = -1;
    };
    const onLeave = () => {
      hoverCount = Math.max(0, hoverCount - 1);
      if (hoverCount === 0) direction = 1;
    };
    children.forEach((c) => {
      c.addEventListener("mouseenter", onEnter);
      c.addEventListener("mouseleave", onLeave);
    });

    const onVis = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
      } else if (!raf) {
        last = performance.now();
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      children.forEach((c) => {
        c.removeEventListener("mouseenter", onEnter);
        c.removeEventListener("mouseleave", onLeave);
      });
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [containerRef, childSelector, periodSeconds, fallbackRadius]);
};
